// Copilote Jamora — pure helpers of the usage dashboard (Paramètres › Consommation du Copilote). Amounts arrive as integer
// nano-dollars (1 $ = 1 000 000 000 n$): they are only formatted here, never added up in floating point.

const NANO = 1_000_000_000;
const numbers = new Intl.NumberFormat("fr-FR");
const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });

export const OUTCOMES = {
  success: { label: "Répondue", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  budget: { label: "Plafond atteint", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  refused: { label: "Refusée", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  error: { label: "Erreur", tone: "bg-red-50 text-red-700 border-red-200" },
  interrupted: { label: "Interrompue", tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const USAGE_MODELS = [
  { value: "claude-haiku-5-5", label: "Claude Haiku 5.5" },
  { value: "claude-sonnet-5-5", label: "Claude Sonnet 5.5" },
  { value: "claude-opus-4-8", label: "Claude Opus 4.8 (secours)" },
];

export const PERIODS = [
  { value: "today", label: "Aujourd'hui" },
  { value: "7d", label: "7 derniers jours" },
  { value: "30d", label: "30 derniers jours" },
  { value: "month", label: "Ce mois-ci" },
  { value: "custom", label: "Personnalisée" },
];

// Same reasons as the server's closed codes (backend/assistant/runner.py and access.py).
export const ERROR_CODES = {
  no_permission: "Sans accès au Copilote",
  account_disabled: "Accès suspendu pour ce compte",
  rate_limited: "Trop de questions en peu de temps",
  monthly_budget: "Plafond mensuel atteint",
  daily_budget: "Plafond du jour atteint",
  not_configured: "Clé API absente",
  refused: "Demande refusée par le modèle",
  truncated: "Réponse coupée",
  invalid_key: "Clé API refusée",
  billing: "Crédit Anthropic épuisé",
  rate_limited_provider: "Limite du fournisseur",
  unreachable: "Fournisseur injoignable",
  provider_error: "Erreur du fournisseur",
  internal: "Erreur interne",
};

// Token series of the stacked chart: categorical slots 1–4 of the validated reference palette (dataviz skill), fixed order.
export const TOKEN_SERIES = [
  { key: "input_tokens", label: "Entrée", color: "#2a78d6" },
  { key: "cache_read_input_tokens", label: "Cache lu", color: "#eb6834" },
  { key: "cache_creation_input_tokens", label: "Cache écrit", color: "#1baf7a" },
  { key: "output_tokens", label: "Sortie", color: "#eda100" },
];
export const SINGLE_SERIES_COLOR = "#083346"; // Jamora blue, one series per chart

export function formatCost(nanodollars) {
  if (nanodollars == null) return "—";
  const dollars = nanodollars / NANO;
  if (dollars === 0) return "0 $";
  if (dollars < 0.0001) return "< 0,0001 $";
  const digits = dollars >= 1 ? 2 : dollars >= 0.01 ? 3 : 4;
  return `${dollars.toFixed(digits).replace(".", ",")} $`;
}

export function formatTokens(count, { short = false } = {}) {
  if (count == null) return "—";
  return short && count >= 100_000 ? compact.format(count) : numbers.format(count);
}

export function formatPercent(rate) {
  if (rate == null) return "—";
  const percent = rate * 100;
  if (percent > 0 && percent < 0.1) return "< 0,1 %";
  return `${(percent > 0 && percent < 10 ? percent.toFixed(1) : Math.round(percent).toString()).replace(".", ",")} %`;
}

export function formatDuration(ms) {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
}

function isoDay(date) {
  return date.toISOString().slice(0, 10);
}

// The server reads UTC calendar days (the CRM's business timezone, Africa/Dakar, is UTC+0).
export function periodRange(period, now = new Date(), custom = {}) {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysBack = (n) => isoDay(new Date(today.getTime() - n * 86_400_000));
  switch (period) {
    case "today": return { from: isoDay(today), to: isoDay(today) };
    case "7d": return { from: daysBack(6), to: isoDay(today) };
    case "month": return { from: isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))), to: isoDay(today) };
    case "custom": return { from: custom.from || isoDay(today), to: custom.to || isoDay(today) };
    default: return { from: daysBack(29), to: isoDay(today) };
  }
}

export function customRangeError({ from, to }) {
  if (!from || !to) return "Choisissez les deux dates.";
  if (from > to) return "La date de début est après la date de fin.";
  if ((Date.parse(to) - Date.parse(from)) / 86_400_000 + 1 > 366) return "Période trop longue : 366 jours au plus.";
  return null;
}

// Only the filters actually set reach the query string.
export function usageParams(range, filters = {}) {
  const params = { from: range.from, to: range.to };
  for (const key of ["user_id", "model", "outcome", "tool"]) if (filters[key]) params[key] = filters[key];
  return params;
}

export function activeFilterCount(filters = {}) {
  return ["user_id", "model", "outcome", "tool"].filter((key) => filters[key]).length;
}

// The monthly cap gauge: share spent, share projected at the current pace, and how worried to be.
export function monthGauge(month) {
  if (!month || !month.budget_nanodollars) return { limited: false };
  const share = month.spent_nanodollars / month.budget_nanodollars;
  const projected = month.projected_nanodollars == null ? null : month.projected_nanodollars / month.budget_nanodollars;
  const level = share >= 1 ? "reached" : projected != null && projected > 1 ? "warning" : "ok";
  return { limited: true, share: Math.min(share, 1), projected, level };
}

// The chronology of one question, as the detail drawer shows it: question, then each model call followed by the tools it
// asked for, then the answer.
export function timeline(detail) {
  if (!detail) return [];
  const steps = [{ kind: "question", at: detail.at }];
  const tools = detail.tool_runs || [];
  for (const call of detail.model_calls || []) {
    steps.push({ kind: "call", ...call });
    for (const run of tools.filter((t) => t.round === call.round)) steps.push({ kind: "tool", ...run });
  }
  if (detail.model_calls?.length) steps.push({ kind: "answer", outcome: detail.outcome, error_code: detail.error_code });
  return steps;
}

export function timeOf(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" });
}

export function dateTimeOf(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    timeZone: "UTC" });
}

export function dayLabel(day) {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
}

// « Aujourd'hui : 12 % de votre plafond » in the panel; null when there is no cap or nothing to say.
export function todayShare(today) {
  if (!today || !today.budget_nanodollars) return null;
  return Math.min(today.spent_nanodollars / today.budget_nanodollars, 1);
}

// Colour of the day's spending ring next to the question field: green, then amber from half the cap, red from 80 %.
export function shareTone(share) {
  if (share == null) return null;
  if (share >= 0.8) return "high";
  if (share >= 0.5) return "warn";
  return "ok";
}

// The tools of a question or of a model call, by their business label (never the technical name, PRODUCT_PRINCIPLES § 3); an
// older answer without labels falls back to the names.
export function toolLabels(item) {
  return item?.tool_labels?.length ? item.tool_labels : [...new Set(item?.tools || [])];
}

// V2: what became of the approval cards a question proposed. `pending` = still waiting for the person's click.
export const ACTION_STATES = {
  accepted: { label: "Acceptée", plural: "acceptées", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refused: { label: "Refusée", plural: "refusées", tone: "bg-slate-100 text-slate-700 border-slate-200" },
  dropped: { label: "Annulée ou expirée", plural: "annulées ou expirées", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  failed: { label: "Échouée", plural: "échouées", tone: "bg-red-50 text-red-700 border-red-200" },
  pending: { label: "En attente", plural: "en attente", tone: "bg-amber-50 text-amber-800 border-amber-200" },
};

// « 3 proposées · 2 acceptées · 1 refusée » — only the states that happened; "" when nothing was proposed.
export function actionSummary(actions) {
  if (!actions?.proposed) return "";
  const parts = [`${actions.proposed} proposée${actions.proposed > 1 ? "s" : ""}`];
  for (const state of ["accepted", "refused", "dropped", "failed"]) {
    const n = actions[state] || 0;
    if (n) parts.push(`${n} ${n > 1 ? ACTION_STATES[state].plural : ACTION_STATES[state].label.toLowerCase()}`);
  }
  return parts.join(" · ");
}
